import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Paper,
  Tabs,
  Tab,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { GridColDef, GridSelectionModel } from "@mui/x-data-grid";
import { Delete as DeleteIcon, Search as SearchIcon } from "@mui/icons-material";
import NavigationHeader from "components/NavigationHeader";
import { StripedDataGrid } from "components/StripedDataGrid";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import { DashboardStackCustom } from "components/StackCustom/styles";
import { useNavigation } from "contexts/notificationsContext";
import BackdropLoading from "components/BackdropLoading";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { SecaoBaseAntiga } from "./components/SecaoBaseAntiga";
import { debounce } from "lodash";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import {
  listarLiberacoesTipologia,
  criarLiberacaoTipologia,
  removerLiberacaoTipologia,
  listarLiberacoesReferencia,
  criarLiberacaoReferencia,
  removerLiberacaoReferencia,
  listarLiberacoesAplicabilidade,
  criarLiberacaoAplicabilidade,
  removerLiberacaoAplicabilidade,
  listarLiberacoesTabelaPreco,
  criarLiberacaoTabelaPreco,
  removerLiberacaoTabelaPreco,
  listarMateriaisDisponiveisN1,
  listarTipologiasDisponiveisN2,
  listarAplicabilidadesDisponiveis,
  listarTabelasPrecoDisponiveis,
  ILiberacaoTipologia,
  ILiberacaoReferencia,
  ILiberacaoAplicabilidade,
  ILiberacaoTabelaPreco,
  IMaterialDisponivelDelphi,
  ITipologiaDisponivelDelphi,
  IAplicabilidadeDisponivelDelphi,
  ITabelaPrecoDisponivelDelphi,
} from "services/modules/liberacoes";
import { api } from "services/axios/api";
import { getEmpresas } from "services/modules/empresas";
import { getProjetistas } from "services/modules/projetistas";
import { IEmpresa } from "shared/interfaces/empresa";
import { IProjetista } from "shared/interfaces/projetista";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import BadgeSituacao from "./components/BadgeSituacao";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`liberacao-tabpanel-${index}`}
      aria-labelledby={`liberacao-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const Liberacoes = () => {
  const { openNotification } = useNavigation();
  const navigate = useNavigate();
  const { empresaId } = useParams<{ empresaId: string }>();
  const location = useLocation();
  const nomeEmpresaFromState = (location.state as any)?.nomeEmpresa;
  const serialFromState = (location.state as any)?.cliente?.serial || (location.state as any)?.serial || "";

  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [liberacoesTipologia, setLiberacoesTipologia] = useState<ILiberacaoTipologia[]>([]);
  const [liberacoesReferencia, setLiberacoesReferencia] = useState<ILiberacaoReferencia[]>([]);
  const [liberacoesAplicabilidade, setLiberacoesAplicabilidade] = useState<ILiberacaoAplicabilidade[]>([]);
  const [liberacoesTabelaPreco, setLiberacoesTabelaPreco] = useState<ILiberacaoTabelaPreco[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [projetistaFiltro, setProjetistaFiltro] = useState<number | "">("");
  const [linhaFiltro, setLinhaFiltro] = useState<number | "">("");
  const [empresaSelecionada, setEmpresaSelecionada] = useState<IEmpresa | null>(null);
  const [empresas, setEmpresas] = useState<IEmpresa[]>([]);
  const [projetistas, setProjetistas] = useState<IProjetista[]>([]);

  // Seleção múltipla nas grids principais
  const [selectionN1, setSelectionN1] = useState<GridSelectionModel>([]);
  const [selectionN2, setSelectionN2] = useState<GridSelectionModel>([]);
  const [selectionAplic, setSelectionAplic] = useState<GridSelectionModel>([]);
  const [selectionTabela, setSelectionTabela] = useState<GridSelectionModel>([]);

  type FilterKeyN1 = "tipoPacote" | "referencia" | "linha" | "data" | "situacao";
  type FilterKeyN2 = "projetista" | "linhaSistema" | "data" | "situacao";
  type FilterKeyAplic = "referencia" | "data" | "situacao";
  type FilterKeyTabela = "descricao" | "data" | "situacao" | "tabelaPreco" | "tipo";

  const [filtersN1, setFiltersN1] = useState<Record<FilterKeyN1, string>>({
    tipoPacote: "",
    referencia: "",
    linha: "",
    data: "",
    situacao: "",
  });

  const [filtersN2, setFiltersN2] = useState<Record<FilterKeyN2, string>>({
    projetista: "",
    linhaSistema: "",
    data: "",
    situacao: "",
  });

  const [filtersAplic, setFiltersAplic] = useState<Record<FilterKeyAplic, string>>({
    referencia: "",
    data: "",
    situacao: "",
  });

  const [filtersTabela, setFiltersTabela] = useState<Record<FilterKeyTabela, string>>({
    descricao: "",
    data: "",
    situacao: "",
    tabelaPreco: "",
    tipo: "",
  });

  const debouncedFilterN1 = useCallback(
    debounce((key: FilterKeyN1, value: string) => {
      setFiltersN1((prev) => ({ ...prev, [key]: value }));
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  const debouncedFilterN2 = useCallback(
    debounce((key: FilterKeyN2, value: string) => {
      setFiltersN2((prev) => ({ ...prev, [key]: value }));
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  const debouncedFilterAplic = useCallback(
    debounce((key: FilterKeyAplic, value: string) => {
      setFiltersAplic((prev) => ({ ...prev, [key]: value }));
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  const debouncedFilterTabela = useCallback(
    debounce((key: FilterKeyTabela, value: string) => {
      setFiltersTabela((prev) => ({ ...prev, [key]: value }));
      setPage(1);
    }, TIME_DEBOUNCE),
    []
  );

  const [modalDelete, setModalDelete] = useState<{
    isOpen: boolean;
    id?: number;
    tipo?: string;
  }>({ isOpen: false });
  const [modalCriar, setModalCriar] = useState<{
    isOpen: boolean;
    tipo?: string;
  }>({ isOpen: false });

  // Estados para modais — dados carregados da API + seleção via checkbox
  const [materiaisDisponiveisN1, setMateriaisDisponiveisN1] = useState<IMaterialDisponivelDelphi[]>([]);
  const [tipologiasDisponiveisN2, setTipologiasDisponiveisN2] = useState<ITipologiaDisponivelDelphi[]>([]);
  const [aplicabilidadesDisponiveisModal, setAplicabilidadesDisponiveisModal] = useState<IAplicabilidadeDisponivelDelphi[]>([]);
  const [tabelasPrecoDisponiveisModal, setTabelasPrecoDisponiveisModal] = useState<ITabelaPrecoDisponivelDelphi[]>([]);
  const [isLoadingModal, setIsLoadingModal] = useState(false);

  // Seleção nos modais
  const [selecaoModalN1, setSelecaoModalN1] = useState<GridSelectionModel>([]);
  const [selecaoModalN2, setSelecaoModalN2] = useState<GridSelectionModel>([]);
  const [selecaoModalAplic, setSelecaoModalAplic] = useState<GridSelectionModel>([]);
  const [selecaoModalTabela, setSelecaoModalTabela] = useState<GridSelectionModel>([]);

  // Abas dos modais
  const [abaModalN1, setAbaModalN1] = useState<"HOMOLOGACAO" | "MERCADO">("HOMOLOGACAO");
  const [abaModalN2, setAbaModalN2] = useState<"VALIDACAO" | "HOMOLOGACAO" | "MERCADO">("VALIDACAO");
  const [abaModalAplic, setAbaModalAplic] = useState<"HOMOLOGACAO" | "MERCADO">("HOMOLOGACAO");
  const [abaModalTabelaPreco, setAbaModalTabelaPreco] = useState<"HOMOLOGACAO" | "MERCADO">("HOMOLOGACAO");

  // Filtros dos modais
  const [filterModalN1, setFilterModalN1] = useState<Record<string, string>>({
    tipoPacote: "", referencia: "", linha: "", data: "",
  });
  const [filterModalN2, setFilterModalN2] = useState<Record<string, string>>({
    projetista: "", linha: "", data: "",
  });
  const [filterModalAplic, setFilterModalAplic] = useState<Record<string, string>>({
    referencia: "", data: "",
  });
  const [filterModalTabela, setFilterModalTabela] = useState<Record<string, string>>({
    descricao: "", empresa: "", data: "",
  });

  const carregarEmpresa = useCallback(async () => {
    if (!empresaId) return;

    setIsLoading(true);
    try {
      const listaEmpresas = await getEmpresas({ page: 1, pageSize: 1000 });
      setEmpresas(listaEmpresas);
      
      const empresa = listaEmpresas.find((e: IEmpresa) => e.idEmpresa === Number(empresaId));
      
      if (empresa) {
        setEmpresaSelecionada(empresa);
      }
    } catch (error) {
      openNotification({
        message: "Erro ao carregar dados da empresa",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [empresaId, openNotification, navigate]);

  const carregarProjetistas = useCallback(async () => {
    try {
      const data = await getProjetistas({ page: 1, pageSize: 1000 });
      setProjetistas(data);
    } catch (error) {
      openNotification({
        message: "Erro ao carregar projetistas",
        type: "error",
      });
    }
  }, [openNotification]);

  useEffect(() => {
    carregarEmpresa();
    carregarProjetistas();
  }, [carregarEmpresa, carregarProjetistas]);

  const getSituacaoByAbaN1 = (aba: string) => {
    return aba === "HOMOLOGACAO" ? "homologação" : "mercado";
  };

  const getSituacaoByAbaN2 = (aba: string) => {
    if (aba === "VALIDACAO") return "validação";
    if (aba === "HOMOLOGACAO") return "homologação";
    return "mercado";
  };

  const carregarMateriaisDisponiveisN1 = useCallback(async () => {
    setIsLoadingModal(true);
    try {
      const data = await listarMateriaisDisponiveisN1({
        empresaId: empresaId ? Number(empresaId) : undefined,
        idSerial: empresaId ? Number(empresaId) : undefined,
        situacao: getSituacaoByAbaN1(abaModalN1),
      });
      setMateriaisDisponiveisN1(data);
    } catch (error) {
      openNotification({ message: "Erro ao carregar materiais disponíveis", type: "error" });
    } finally {
      setIsLoadingModal(false);
    }
  }, [empresaId, abaModalN1, openNotification]);

  const carregarTipologiasDisponiveisN2 = useCallback(async () => {
    setIsLoadingModal(true);
    try {
      const data = await listarTipologiasDisponiveisN2({
        empresaId: empresaId ? Number(empresaId) : undefined,
        idSerial: empresaId ? Number(empresaId) : undefined,
        situacao: getSituacaoByAbaN2(abaModalN2),
      });
      setTipologiasDisponiveisN2(data);
    } catch (error) {
      openNotification({ message: "Erro ao carregar tipologias disponíveis", type: "error" });
    } finally {
      setIsLoadingModal(false);
    }
  }, [empresaId, abaModalN2, openNotification]);

  const carregarAplicabilidadesDisponiveisModal = useCallback(async () => {
    setIsLoadingModal(true);
    try {
      const data = await listarAplicabilidadesDisponiveis({
        empresaId: empresaId ? Number(empresaId) : undefined,
        idSerial: empresaId ? Number(empresaId) : undefined,
        situacao: getSituacaoByAbaN1(abaModalAplic),
      });
      setAplicabilidadesDisponiveisModal(data);
    } catch (error) {
      openNotification({ message: "Erro ao carregar aplicabilidades disponíveis", type: "error" });
    } finally {
      setIsLoadingModal(false);
    }
  }, [empresaId, abaModalAplic, openNotification]);

  const carregarTabelasPrecoDisponiveisModal = useCallback(async () => {
    setIsLoadingModal(true);
    try {
      const data = await listarTabelasPrecoDisponiveis({
        empresaId: empresaId ? Number(empresaId) : undefined,
        idSerial: empresaId ? Number(empresaId) : undefined,
        situacao: getSituacaoByAbaN1(abaModalTabelaPreco),
      });
      setTabelasPrecoDisponiveisModal(data);
    } catch (error) {
      openNotification({ message: "Erro ao carregar tabelas de preço disponíveis", type: "error" });
    } finally {
      setIsLoadingModal(false);
    }
  }, [empresaId, abaModalTabelaPreco, openNotification]);

  useEffect(() => {
    if (modalCriar.isOpen && modalCriar.tipo === "referencia") {
      carregarMateriaisDisponiveisN1();
      setSelecaoModalN1([]);
      setFilterModalN1({ tipoPacote: "", referencia: "", linha: "", data: "" });
    }
  }, [modalCriar.isOpen, modalCriar.tipo, abaModalN1, carregarMateriaisDisponiveisN1]);

  useEffect(() => {
    if (modalCriar.isOpen && modalCriar.tipo === "tipologia") {
      carregarTipologiasDisponiveisN2();
      setSelecaoModalN2([]);
      setFilterModalN2({ projetista: "", linha: "", data: "" });
    }
  }, [modalCriar.isOpen, modalCriar.tipo, abaModalN2, carregarTipologiasDisponiveisN2]);

  useEffect(() => {
    if (modalCriar.isOpen && modalCriar.tipo === "aplicabilidade") {
      carregarAplicabilidadesDisponiveisModal();
      setSelecaoModalAplic([]);
      setFilterModalAplic({ referencia: "", data: "" });
    }
  }, [modalCriar.isOpen, modalCriar.tipo, abaModalAplic, carregarAplicabilidadesDisponiveisModal]);

  useEffect(() => {
    if (modalCriar.isOpen && modalCriar.tipo === "tabelaPreco") {
      carregarTabelasPrecoDisponiveisModal();
      setSelecaoModalTabela([]);
      setFilterModalTabela({ descricao: "", empresa: "", data: "" });
    }
  }, [modalCriar.isOpen, modalCriar.tipo, abaModalTabelaPreco, carregarTabelasPrecoDisponiveisModal]);

  const carregarLiberacoesTipologia = useCallback(async () => {
    if (!empresaId) {
      setLiberacoesTipologia([]);
      setTotalRecords(0);
      return;
    }

    setIsLoading(true);
    try {
      const response = await listarLiberacoesTipologia({
        // O parâmetro de rota "empresaId" desta tela carrega o ID DO SERIAL —
        // a listagem filtra por idSerial (o backend resolve as empresas do serial).
        idSerial: Number(empresaId),
        projetistaId: projetistaFiltro ? Number(projetistaFiltro) : undefined,
        linhaId: linhaFiltro ? Number(linhaFiltro) : undefined,
        page,
        limit: pageSize,
      });
      setLiberacoesTipologia(response.items || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      openNotification({
        message: "Erro ao carregar liberações de tipologias",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [empresaId, projetistaFiltro, linhaFiltro, page, pageSize, openNotification]);

  const carregarLiberacoesReferencia = useCallback(async () => {
    if (!empresaId) {
      setLiberacoesReferencia([]);
      setTotalRecords(0);
      return;
    }

    setIsLoading(true);
    try {
      const response = await listarLiberacoesReferencia({
        empresaId: Number(empresaId),
        idSerial: Number(empresaId),
        page,
        limit: pageSize,
      });
      setLiberacoesReferencia(response.items || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      openNotification({
        message: "Erro ao carregar liberações de referências",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [empresaId, page, pageSize, openNotification]);

  const carregarLiberacoesAplicabilidade = useCallback(async () => {
    if (!empresaId) {
      setLiberacoesAplicabilidade([]);
      setTotalRecords(0);
      return;
    }

    setIsLoading(true);
    try {
      const response = await listarLiberacoesAplicabilidade({
        empresaId: Number(empresaId),
        idSerial: Number(empresaId),
        page,
        limit: pageSize,
      });
      setLiberacoesAplicabilidade(response.items || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      openNotification({
        message: "Erro ao carregar liberações de aplicabilidades",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [empresaId, page, pageSize, openNotification]);

  const carregarLiberacoesTabelaPreco = useCallback(async () => {
    if (!empresaId) {
      setLiberacoesTabelaPreco([]);
      setTotalRecords(0);
      return;
    }

    setIsLoading(true);
    try {
      const response = await listarLiberacoesTabelaPreco({
        empresaId: Number(empresaId),
        idSerial: Number(empresaId),
        page,
        limit: pageSize,
      });
      setLiberacoesTabelaPreco(response.items || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      openNotification({
        message: "Erro ao carregar liberações de tabelas de preço",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [empresaId, page, pageSize, openNotification]);

  useEffect(() => {
    if (tabValue === 0) {
      carregarLiberacoesReferencia();
    } else if (tabValue === 1) {
      carregarLiberacoesTipologia();
    } else if (tabValue === 2) {
      carregarLiberacoesAplicabilidade();
    } else if (tabValue === 3) {
      carregarLiberacoesTabelaPreco();
    }
  }, [tabValue, carregarLiberacoesTipologia, carregarLiberacoesReferencia, carregarLiberacoesAplicabilidade, carregarLiberacoesTabelaPreco]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  // Salvar selecionados nos modais
  const handleSalvarN1 = async () => {
    if (selecaoModalN1.length === 0) {
      openNotification({ message: "Selecione pelo menos um item", type: "warning" });
      return;
    }

    setIsLoading(true);
    let sucessos = 0;
    let erros = 0;
    const situacao = abaModalN1 === "HOMOLOGACAO" ? "Homologação" : "Disponível Mercado";

    try {
      for (const itemId of selecaoModalN1) {
        const item = materiaisDisponiveisN1.find((m) => m.id === itemId);
        if (!item) continue;
        const parts = item.id.split("_");
        const referenciaId = Number(parts[0]);
        const linhaId = Number(parts[1]);

        try {
          await criarLiberacaoReferencia({
            empresaId: Number(empresaId),
            idSerial: Number(empresaId),
            referenciaId,
            linhaId,
            situacao,
          });
          sucessos++;
        } catch {
          erros++;
        }
      }

      if (sucessos > 0) {
        openNotification({
          message: `${sucessos} cadastro(s) liberado(s) com sucesso!${erros > 0 ? ` (${erros} falha(s))` : ""}`,
          type: sucessos === selecaoModalN1.length ? "success" : "warning",
        });
      }

      setModalCriar({ isOpen: false });
      setSelecaoModalN1([]);
      carregarLiberacoesReferencia();
    } catch (error) {
      openNotification({ message: "Erro ao salvar liberações", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvarN2 = async () => {
    if (selecaoModalN2.length === 0) {
      openNotification({ message: "Selecione pelo menos um item", type: "warning" });
      return;
    }

    setIsLoading(true);
    let sucessos = 0;
    let erros = 0;
    let situacao = "Validação";
    if (abaModalN2 === "HOMOLOGACAO") situacao = "Homologação";
    else if (abaModalN2 === "MERCADO") situacao = "Disponível Mercado";

    try {
      for (const itemId of selecaoModalN2) {
        const item = tipologiasDisponiveisN2.find((t) => t.id === itemId);
        if (!item) continue;
        const parts = item.id.split("_");
        const projetistaId = Number(parts[0]);
        const linhaId = Number(parts[1]);

        try {
          // O parâmetro de rota "empresaId" desta tela carrega o ID DO SERIAL.
          // Liberação escopada ao serial grava IdEmpresa=0 (convenção do item 5-A);
          // antes o id do serial vazava para o campo empresa (bug 4 do bugs_fak, 16/07/2026).
          await criarLiberacaoTipologia({
            empresaId: 0,
            idSerial: Number(empresaId),
            projetistaId,
            linhaId,
            situacao,
          });
          sucessos++;
        } catch {
          erros++;
        }
      }

      if (sucessos > 0) {
        openNotification({
          message: `${sucessos} tipologia(s) liberada(s) com sucesso!${erros > 0 ? ` (${erros} falha(s))` : ""}`,
          type: sucessos === selecaoModalN2.length ? "success" : "warning",
        });
      } else if (erros > 0) {
        openNotification({
          message: `Falha ao salvar ${erros} liberação(ões). Verifique os dados e tente novamente.`,
          type: "error",
        });
      }

      setModalCriar({ isOpen: false });
      setSelecaoModalN2([]);
      carregarLiberacoesTipologia();
    } catch (error) {
      openNotification({ message: "Erro ao salvar liberações", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvarAplic = async () => {
    if (selecaoModalAplic.length === 0) {
      openNotification({ message: "Selecione pelo menos um item", type: "warning" });
      return;
    }

    setIsLoading(true);
    let sucessos = 0;
    let erros = 0;
    const situacao = abaModalAplic === "HOMOLOGACAO" ? "Homologação" : "Disponível Mercado";

    try {
      for (const itemId of selecaoModalAplic) {
        try {
          await criarLiberacaoAplicabilidade({
            empresaId: Number(empresaId),
            idSerial: Number(empresaId),
            aplicabilidadeId: Number(itemId),
            situacao,
          });
          sucessos++;
        } catch {
          erros++;
        }
      }

      if (sucessos > 0) {
        openNotification({
          message: `${sucessos} aplicabilidade(s) liberada(s) com sucesso!${erros > 0 ? ` (${erros} falha(s))` : ""}`,
          type: sucessos === selecaoModalAplic.length ? "success" : "warning",
        });
      }

      setModalCriar({ isOpen: false });
      setSelecaoModalAplic([]);
      carregarLiberacoesAplicabilidade();
    } catch (error) {
      openNotification({ message: "Erro ao salvar liberações", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSalvarTabela = async () => {
    if (selecaoModalTabela.length === 0) {
      openNotification({ message: "Selecione pelo menos um item", type: "warning" });
      return;
    }

    setIsLoading(true);
    let sucessos = 0;
    let erros = 0;
    const situacao = abaModalTabelaPreco === "HOMOLOGACAO" ? "Em Homologação" : "Disponível Mercado";

    try {
      for (const itemId of selecaoModalTabela) {
        try {
          // O serviço devolve null em erro (ex.: tabela já liberada) em vez de lançar.
          const resultado = await criarLiberacaoTabelaPreco({
            empresaId: Number(empresaId),
            idSerial: Number(empresaId),
            tabelaPrecoId: Number(itemId),
            situacao,
          });
          if (resultado === null) erros++;
          else sucessos++;
        } catch {
          erros++;
        }
      }

      if (sucessos > 0) {
        openNotification({
          message: `${sucessos} tabela(s) de preço liberada(s) com sucesso!${erros > 0 ? ` (${erros} falha(s))` : ""}`,
          type: sucessos === selecaoModalTabela.length ? "success" : "warning",
        });
      }

      setModalCriar({ isOpen: false });
      setSelecaoModalTabela([]);
      carregarLiberacoesTabelaPreco();
    } catch (error) {
      openNotification({ message: "Erro ao salvar liberações", type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoverLiberacao = async () => {
    if (!modalDelete.id || !modalDelete.tipo) return;

    setIsLoading(true);
    try {
      if (modalDelete.tipo === "tipologia") {
        await removerLiberacaoTipologia(modalDelete.id);
      } else if (modalDelete.tipo === "referencia") {
        await removerLiberacaoReferencia(modalDelete.id);
      } else if (modalDelete.tipo === "aplicabilidade") {
        await removerLiberacaoAplicabilidade(modalDelete.id);
      } else if (modalDelete.tipo === "tabelaPreco") {
        await removerLiberacaoTabelaPreco(modalDelete.id);
      }

      openNotification({
        message: "Liberação excluída com sucesso!",
        type: "success",
      });
      setModalDelete({ isOpen: false });

      if (modalDelete.tipo === "tipologia") carregarLiberacoesTipologia();
      else if (modalDelete.tipo === "referencia") carregarLiberacoesReferencia();
      else if (modalDelete.tipo === "aplicabilidade") carregarLiberacoesAplicabilidade();
      else if (modalDelete.tipo === "tabelaPreco") carregarLiberacoesTabelaPreco();
    } catch (error) {
      // Erro já tratado no serviço
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoverSelecionados = async (tipo: string, ids: GridSelectionModel) => {
    if (ids.length === 0) {
      openNotification({ message: "Selecione pelo menos um item para remover", type: "warning" });
      return;
    }

    setIsLoading(true);
    let sucessos = 0;
    let erros = 0;

    try {
      for (const id of ids) {
        try {
          if (tipo === "referencia") await removerLiberacaoReferencia(Number(id));
          else if (tipo === "tipologia") await removerLiberacaoTipologia(Number(id));
          else if (tipo === "aplicabilidade") await removerLiberacaoAplicabilidade(Number(id));
          else if (tipo === "tabelaPreco") await removerLiberacaoTabelaPreco(Number(id));
          sucessos++;
        } catch {
          erros++;
        }
      }

      if (sucessos > 0) {
        openNotification({
          message: `${sucessos} liberação(ões) removida(s)!${erros > 0 ? ` (${erros} falha(s))` : ""}`,
          type: "success",
        });
      }

      if (tipo === "referencia") { setSelectionN1([]); carregarLiberacoesReferencia(); }
      else if (tipo === "tipologia") { setSelectionN2([]); carregarLiberacoesTipologia(); }
      else if (tipo === "aplicabilidade") { setSelectionAplic([]); carregarLiberacoesAplicabilidade(); }
      else if (tipo === "tabelaPreco") { setSelectionTabela([]); carregarLiberacoesTabelaPreco(); }
    } finally {
      setIsLoading(false);
    }
  };

  // Filtragem local
  const filteredLiberacoesReferencia = useMemo(() => {
    return liberacoesReferencia.filter((liberacao) => {
      const tipoPacote = (liberacao as any).tipoPacote?.toLowerCase() || "referência";
      const referencia = (liberacao.referenciaCodigo || "").toLowerCase();
      const linha = (liberacao.linhaNome || "").toLowerCase();
      const data = liberacao.dataLiberacao
        ? new Date(liberacao.dataLiberacao).toLocaleDateString("pt-BR").toLowerCase()
        : "";
      const situacao = (liberacao.situacao || "").toLowerCase();

      if (filtersN1.tipoPacote && !tipoPacote.includes(filtersN1.tipoPacote.toLowerCase())) return false;
      if (filtersN1.referencia && !referencia.includes(filtersN1.referencia.toLowerCase())) return false;
      if (filtersN1.linha && !linha.includes(filtersN1.linha.toLowerCase())) return false;
      if (filtersN1.data && !data.includes(filtersN1.data.toLowerCase())) return false;
      if (filtersN1.situacao && !situacao.includes(filtersN1.situacao.toLowerCase())) return false;

      return true;
    });
  }, [liberacoesReferencia, filtersN1]);

  const filteredLiberacoesTipologia = useMemo(() => {
    return liberacoesTipologia.filter((liberacao) => {
      const projetistaNome = liberacao.projetistaNome
        ? liberacao.projetistaNome.toLowerCase()
        : projetistas.find((p) => p.idProjetista === liberacao.projetistaId)?.nome.toLowerCase() || "";
      const linhaSistema = (liberacao.linhaNome || "").toLowerCase();
      const data = liberacao.dataLiberacao
        ? new Date(liberacao.dataLiberacao).toLocaleDateString("pt-BR").toLowerCase()
        : "";
      const situacao = (liberacao.situacao || "").toLowerCase();

      if (filtersN2.projetista && !projetistaNome.includes(filtersN2.projetista.toLowerCase())) return false;
      if (filtersN2.linhaSistema && !linhaSistema.includes(filtersN2.linhaSistema.toLowerCase())) return false;
      if (filtersN2.data && !data.includes(filtersN2.data.toLowerCase())) return false;
      if (filtersN2.situacao && !situacao.includes(filtersN2.situacao.toLowerCase())) return false;

      return true;
    });
  }, [liberacoesTipologia, filtersN2, projetistas]);

  const filteredLiberacoesAplicabilidade = useMemo(() => {
    return liberacoesAplicabilidade.filter((liberacao) => {
      const referencia = (liberacao.aplicabilidadeDescricao || "").toLowerCase();
      const data = liberacao.dataLiberacao
        ? new Date(liberacao.dataLiberacao).toLocaleDateString("pt-BR").toLowerCase()
        : "";
      const situacao = (liberacao.situacao || "").toLowerCase();

      if (filtersAplic.referencia && !referencia.includes(filtersAplic.referencia.toLowerCase())) return false;
      if (filtersAplic.data && !data.includes(filtersAplic.data.toLowerCase())) return false;
      if (filtersAplic.situacao && !situacao.includes(filtersAplic.situacao.toLowerCase())) return false;

      return true;
    });
  }, [liberacoesAplicabilidade, filtersAplic]);

  const filteredLiberacoesTabelaPreco = useMemo(() => {
    return liberacoesTabelaPreco.filter((liberacao) => {
      const data = liberacao.dataLiberacao
        ? new Date(liberacao.dataLiberacao).toLocaleDateString("pt-BR").toLowerCase()
        : "";
      const situacao = (liberacao.situacao || "").toLowerCase();
      const tabelaPreco = (liberacao.tabelaPrecoNome || "").toLowerCase();
      const tipo = (liberacao.tipoMaterial || "").toLowerCase();

      if (filtersTabela.tipo && !tipo.includes(filtersTabela.tipo.toLowerCase())) return false;
      if (filtersTabela.data && !data.includes(filtersTabela.data.toLowerCase())) return false;
      if (filtersTabela.situacao && !situacao.includes(filtersTabela.situacao.toLowerCase())) return false;
      if (filtersTabela.tabelaPreco && !tabelaPreco.includes(filtersTabela.tabelaPreco.toLowerCase())) return false;

      return true;
    });
  }, [liberacoesTabelaPreco, filtersTabela]);

  // Filtragem local nos modais
  const filteredMateriaisN1 = useMemo(() => {
    return materiaisDisponiveisN1.filter((m) => {
      if (filterModalN1.tipoPacote && !m.tipoPacote.toLowerCase().includes(filterModalN1.tipoPacote.toLowerCase())) return false;
      if (filterModalN1.referencia && !m.referencia.toLowerCase().includes(filterModalN1.referencia.toLowerCase())) return false;
      if (filterModalN1.linha && !m.linha.toLowerCase().includes(filterModalN1.linha.toLowerCase())) return false;
      if (filterModalN1.data && !m.data.toLowerCase().includes(filterModalN1.data.toLowerCase())) return false;
      return true;
    });
  }, [materiaisDisponiveisN1, filterModalN1]);

  const filteredTipologiasN2 = useMemo(() => {
    return tipologiasDisponiveisN2.filter((t) => {
      if (filterModalN2.projetista && !t.projetista.toLowerCase().includes(filterModalN2.projetista.toLowerCase())) return false;
      if (filterModalN2.linha && !t.linha.toLowerCase().includes(filterModalN2.linha.toLowerCase())) return false;
      if (filterModalN2.data && !t.data.toLowerCase().includes(filterModalN2.data.toLowerCase())) return false;
      return true;
    });
  }, [tipologiasDisponiveisN2, filterModalN2]);

  const filteredAplicDisp = useMemo(() => {
    return aplicabilidadesDisponiveisModal.filter((a) => {
      if (filterModalAplic.referencia && !a.referencia.toLowerCase().includes(filterModalAplic.referencia.toLowerCase())) return false;
      if (filterModalAplic.data && !a.data.toLowerCase().includes(filterModalAplic.data.toLowerCase())) return false;
      return true;
    });
  }, [aplicabilidadesDisponiveisModal, filterModalAplic]);

  const filteredTabelaDisp = useMemo(() => {
    return tabelasPrecoDisponiveisModal.filter((t) => {
      if (filterModalTabela.descricao && !t.descricao.toLowerCase().includes(filterModalTabela.descricao.toLowerCase())) return false;
      if (filterModalTabela.empresa && !(t.empresa || "").toLowerCase().includes(filterModalTabela.empresa.toLowerCase())) return false;
      if (filterModalTabela.data && !t.data.toLowerCase().includes(filterModalTabela.data.toLowerCase())) return false;
      return true;
    });
  }, [tabelasPrecoDisponiveisModal, filterModalTabela]);

  // Colunas das grids principais
  const columnsTipologia: GridColDef[] = [
    {
      field: "projetistaNome",
      headerName: "Projetista",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn label="Projetista" name="projetista" onChange={(event) => debouncedFilterN2("projetista", event.target.value)} />
      ),
      valueGetter: (params) => {
        if (params.row.projetistaNome) return params.row.projetistaNome;
        const projetista = projetistas.find((p) => p.idProjetista === params.row.projetistaId);
        return projetista ? projetista.nome : "-";
      },
    },
    {
      field: "linhaNome",
      headerName: "Linha/Sistema",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn label="Linha/Sistema" name="linhaSistema" onChange={(event) => debouncedFilterN2("linhaSistema", event.target.value)} />
      ),
      valueGetter: (params) => params.row.linhaNome || "-",
    },
    {
      field: "dataLiberacao",
      headerName: "Data",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Data" name="data" onChange={(event) => debouncedFilterN2("data", event.target.value)} />
      ),
      valueFormatter: (params) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleDateString("pt-BR");
      },
    },
    {
      field: "situacao",
      headerName: "Situação",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Situação" name="situacao" onChange={(event) => debouncedFilterN2("situacao", event.target.value)} />
      ),
      renderCell: (params) => <BadgeSituacao situacao={params.value} />,
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="error" onClick={() => setModalDelete({ isOpen: true, id: params.row.id, tipo: "tipologia" })}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const columnsReferencia: GridColDef[] = [
    {
      field: "tipoPacote",
      headerName: "Tipo de Pacote",
      flex: 0.8,
      minWidth: 130,
      renderHeader: () => (
        <FilterColumn label="Tipo de Pacote" name="tipoPacote" onChange={(event) => debouncedFilterN1("tipoPacote", event.target.value)} />
      ),
      valueGetter: (params) => params.row.tipoPacote || "Referência",
    },
    {
      field: "referenciaCodigo",
      headerName: "Referência",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn label="Referência" name="referencia" onChange={(event) => debouncedFilterN1("referencia", event.target.value)} />
      ),
      valueGetter: (params) => params.row.referenciaCodigo || params.row.referenciaNome || "-",
    },
    {
      field: "linhaNome",
      headerName: "Linha",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn label="Linha" name="linha" onChange={(event) => debouncedFilterN1("linha", event.target.value)} />
      ),
      valueGetter: (params) => params.row.linhaNome || "-",
    },
    {
      field: "dataLiberacao",
      headerName: "Data",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Data" name="data" onChange={(event) => debouncedFilterN1("data", event.target.value)} />
      ),
      valueFormatter: (params) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleDateString("pt-BR");
      },
    },
    {
      field: "situacao",
      headerName: "Situação",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Situação" name="situacao" onChange={(event) => debouncedFilterN1("situacao", event.target.value)} />
      ),
      renderCell: (params) => <BadgeSituacao situacao={params.value} />,
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="error" onClick={() => setModalDelete({ isOpen: true, id: params.row.id, tipo: "referencia" })}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const columnsAplicabilidade: GridColDef[] = [
    {
      field: "aplicabilidadeDescricao",
      headerName: "Referência",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <FilterColumn label="Referência" name="referencia" onChange={(event) => debouncedFilterAplic("referencia", event.target.value)} />
      ),
      valueGetter: (params) => params.row.aplicabilidadeDescricao || "-",
    },
    {
      field: "dataLiberacao",
      headerName: "Data",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Data" name="data" onChange={(event) => debouncedFilterAplic("data", event.target.value)} />
      ),
      valueFormatter: (params) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleDateString("pt-BR");
      },
    },
    {
      field: "situacao",
      headerName: "Situação",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Situação" name="situacao" onChange={(event) => debouncedFilterAplic("situacao", event.target.value)} />
      ),
      renderCell: (params) => <BadgeSituacao situacao={params.value} />,
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="error" onClick={() => setModalDelete({ isOpen: true, id: params.row.id, tipo: "aplicabilidade" })}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const columnsTabelaPreco: GridColDef[] = [
    {
      field: "tabelaPrecoNome",
      headerName: "Tabela de Preço",
      flex: 1.2,
      minWidth: 180,
      renderHeader: () => (
        <FilterColumn label="Tabela de Preço" name="tabelaPreco" onChange={(event) => debouncedFilterTabela("tabelaPreco", event.target.value)} />
      ),
      valueGetter: (params) => params.row.tabelaPrecoNome || params.row.descricao || "-",
    },
    {
      field: "empresaNome",
      headerName: "Empresa",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params.row.empresaNome || "-",
    },
    {
      // Plano 15/10/2026 (Frente 7): uma empresa pode ter uma tabela de cada tipo de material.
      field: "tipoMaterial",
      headerName: "Tipo",
      flex: 0.7,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Tipo" name="tipo" onChange={(event) => debouncedFilterTabela("tipo", event.target.value)} />
      ),
      valueGetter: (params) => params.row.tipoMaterial || "-",
    },
    {
      field: "dataLiberacao",
      headerName: "Data",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Data" name="data" onChange={(event) => debouncedFilterTabela("data", event.target.value)} />
      ),
      valueFormatter: (params) => {
        if (!params.value) return "-";
        return new Date(params.value).toLocaleDateString("pt-BR");
      },
    },
    {
      field: "situacao",
      headerName: "Situação",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <FilterColumn label="Situação" name="situacao" onChange={(event) => debouncedFilterTabela("situacao", event.target.value)} />
      ),
      renderCell: (params) => <BadgeSituacao situacao={params.value} />,
    },
    {
      field: "acoes",
      headerName: "Ações",
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" color="error" onClick={() => setModalDelete({ isOpen: true, id: params.row.id, tipo: "tabelaPreco" })}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  // Colunas para os modais (tabelas selecionáveis)
  const columnsModalN1: GridColDef[] = [
    {
      field: "tipoPacote",
      headerName: "Tipo material",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalN1((prev) => ({ ...prev, tipoPacote: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
    {
      field: "referencia",
      headerName: "Referência",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalN1((prev) => ({ ...prev, referencia: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
    {
      field: "linha",
      headerName: "Linha",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalN1((prev) => ({ ...prev, linha: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
    {
      field: "data",
      headerName: "Data",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalN1((prev) => ({ ...prev, data: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
  ];

  const columnsModalN2: GridColDef[] = [
    {
      field: "projetista",
      headerName: "Projetista",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalN2((prev) => ({ ...prev, projetista: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
    {
      field: "linha",
      headerName: "Linha/Sistema",
      flex: 1,
      minWidth: 150,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalN2((prev) => ({ ...prev, linha: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
    {
      field: "data",
      headerName: "Data",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalN2((prev) => ({ ...prev, data: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
  ];

  const columnsModalAplic: GridColDef[] = [
    {
      field: "referencia",
      headerName: "Referência",
      flex: 1,
      minWidth: 200,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalAplic((prev) => ({ ...prev, referencia: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
    {
      field: "data",
      headerName: "Data",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalAplic((prev) => ({ ...prev, data: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
  ];

  const columnsModalTabela: GridColDef[] = [
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 1,
      minWidth: 200,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalTabela((prev) => ({ ...prev, descricao: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
    {
      field: "empresa",
      headerName: "Empresa",
      flex: 0.9,
      minWidth: 150,
      renderHeader: () => (
        <TextField size="small" placeholder="Empresa" variant="standard"
          onChange={(e) => setFilterModalTabela((prev) => ({ ...prev, empresa: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
    {
      field: "tipoMaterial",
      headerName: "Tipo",
      flex: 0.5,
      minWidth: 110,
    },
    {
      field: "data",
      headerName: "Data",
      flex: 0.8,
      minWidth: 120,
      renderHeader: () => (
        <TextField size="small" placeholder="Faça uma busca" variant="standard"
          onChange={(e) => setFilterModalTabela((prev) => ({ ...prev, data: e.target.value }))}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
      ),
    },
  ];

  const tabStyle = {
    "& .MuiTab-root": {
      color: "#75B0C7",
      backgroundColor: "rgba(235, 244, 249, 1)",
      "&.Mui-selected": {
        color: "#2F7EA0",
        backgroundColor: "rgba(47, 126, 160, 0.15)",
        fontWeight: 600,
      },
    },
    "& .MuiTabs-indicator": {
      backgroundColor: "#2F7EA0",
    },
  };

  return (
    <DashboardStackCustom>
      <BackdropLoading isOpen={isLoading} />
      
      <NavigationHeader
        title={`LIBERAÇÕES > ${empresaSelecionada?.nomeFantasia || empresaSelecionada?.razaoSocial || nomeEmpresaFromState || "EMPRESA LTDA"}`}
        label="Home > Cadastros"
        hasBackButton={true}
        goBack={() => navigate("/liberacoesMof")}
        buttonLabel="ADICIONAR"
        buttonOnClick={() => {
          const tipos = ["referencia", "tipologia", "aplicabilidade", "tabelaPreco"];
          setModalCriar({ isOpen: true, tipo: tipos[tabValue] });
        }}
      />

      <Paper elevation={3}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="tabs de liberações" sx={tabStyle}>
            <Tab label="CADASTROS N1" />
            <Tab label="CADASTROS N2" />
            <Tab label="APLICABILIDADES" />
            <Tab label="TABELAS DE PREÇOS" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2, bgcolor: "background.paper" }}>
          <Typography variant="body2" color="textSecondary">
            {[
              "Lista de cadastros N1 liberados",
              "Lista de tipologias liberadas",
              "Lista de aplicabilidades liberadas",
              "Lista de tabelas de preços liberadas",
            ][tabValue]}{" "}
            para o serial: {serialFromState || empresaSelecionada?.razaoSocial || ""}
          </Typography>
        </Box>

        {/* CADASTROS N1 */}
        <TabPanel value={tabValue} index={0}>
          <Box>
            {selectionN1.length > 0 && (
              <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                <Button variant="outlined" color="error" size="small"
                  onClick={() => handleRemoverSelecionados("referencia", selectionN1)}
                >
                  Remover selecionados ({selectionN1.length})
                </Button>
              </Box>
            )}
            <StripedDataGrid
              columns={columnsReferencia}
              rows={filteredLiberacoesReferencia}
              getRowId={(row) => row.id}
              loading={isLoading}
              page={page - 1}
              pageSize={pageSize}
              rowCount={filteredLiberacoesReferencia.length}
              onPageChange={(newPage: number) => setPage(newPage + 1)}
              onPageSizeChange={(newPageSize: number) => { setPageSize(newPageSize); setPage(1); }}
              checkboxSelection={true}
              onSelectionModelChange={(newSelection: GridSelectionModel) => setSelectionN1(newSelection)}
              selectionModel={selectionN1}
            />
            {/* Plano 22/10/2026 (rev. 2, Frente 4): base antiga do serial, só consulta. */}
            <SecaoBaseAntiga idSerial={Number(empresaId)} nivel={1} />
          </Box>
        </TabPanel>

        {/* CADASTROS N2 */}
        <TabPanel value={tabValue} index={1}>
          <Box>
            {selectionN2.length > 0 && (
              <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                <Button variant="outlined" color="error" size="small"
                  onClick={() => handleRemoverSelecionados("tipologia", selectionN2)}
                >
                  Remover selecionados ({selectionN2.length})
                </Button>
              </Box>
            )}
            <StripedDataGrid
              columns={columnsTipologia}
              rows={filteredLiberacoesTipologia}
              getRowId={(row) => row.id}
              loading={isLoading}
              page={page - 1}
              pageSize={pageSize}
              rowCount={filteredLiberacoesTipologia.length}
              onPageChange={(newPage: number) => setPage(newPage + 1)}
              onPageSizeChange={(newPageSize: number) => { setPageSize(newPageSize); setPage(1); }}
              checkboxSelection={true}
              onSelectionModelChange={(newSelection: GridSelectionModel) => setSelectionN2(newSelection)}
              selectionModel={selectionN2}
            />
            <SecaoBaseAntiga idSerial={Number(empresaId)} nivel={2} />
          </Box>
        </TabPanel>

        {/* APLICABILIDADES */}
        <TabPanel value={tabValue} index={2}>
          <Box>
            {selectionAplic.length > 0 && (
              <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                <Button variant="outlined" color="error" size="small"
                  onClick={() => handleRemoverSelecionados("aplicabilidade", selectionAplic)}
                >
                  Remover selecionados ({selectionAplic.length})
                </Button>
              </Box>
            )}
            <StripedDataGrid
              columns={columnsAplicabilidade}
              rows={filteredLiberacoesAplicabilidade}
              getRowId={(row) => row.id}
              loading={isLoading}
              page={page - 1}
              pageSize={pageSize}
              rowCount={filteredLiberacoesAplicabilidade.length}
              onPageChange={(newPage: number) => setPage(newPage + 1)}
              onPageSizeChange={(newPageSize: number) => { setPageSize(newPageSize); setPage(1); }}
              checkboxSelection={true}
              onSelectionModelChange={(newSelection: GridSelectionModel) => setSelectionAplic(newSelection)}
              selectionModel={selectionAplic}
            />
          </Box>
        </TabPanel>

        {/* TABELAS DE PREÇOS — reativada pelo plano de 30/09/2026 (decisão do PO de 28/09) */}
        <TabPanel value={tabValue} index={3}>
          <Box>
            {selectionTabela.length > 0 && (
              <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
                <Button variant="outlined" color="error" size="small"
                  onClick={() => handleRemoverSelecionados("tabelaPreco", selectionTabela)}
                >
                  Remover selecionados ({selectionTabela.length})
                </Button>
              </Box>
            )}
            <StripedDataGrid
              columns={columnsTabelaPreco}
              rows={filteredLiberacoesTabelaPreco}
              getRowId={(row) => row.id}
              loading={isLoading}
              page={page - 1}
              pageSize={pageSize}
              rowCount={totalRecords}
              onPageChange={(newPage: number) => setPage(newPage + 1)}
              onPageSizeChange={(newPageSize: number) => { setPageSize(newPageSize); setPage(1); }}
              checkboxSelection={true}
              onSelectionModelChange={(newSelection: GridSelectionModel) => setSelectionTabela(newSelection)}
              selectionModel={selectionTabela}
            />
          </Box>
        </TabPanel>

      </Paper>

      {/* Modal CADASTROS N2 — tabela selecionável com 3 abas */}
      <Dialog
        open={modalCriar.isOpen && modalCriar.tipo === "tipologia"}
        onClose={() => { setModalCriar({ isOpen: false }); setSelecaoModalN2([]); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxWidth: "900px" } }}
      >
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>
          Adicionar materiais para o CEM Desktop
        </DialogTitle>
        <DialogContent>
          <Tabs value={abaModalN2} onChange={(e, newValue) => setAbaModalN2(newValue)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2, ...tabStyle }}>
            <Tab label="VALIDAÇÃO" value="VALIDACAO" />
            <Tab label="HOMOLOGAÇÃO" value="HOMOLOGACAO" />
            <Tab label="MERCADO" value="MERCADO" />
          </Tabs>

          <Box sx={{ height: 400 }}>
            <StripedDataGrid
              columns={columnsModalN2}
              rows={filteredTipologiasN2}
              getRowId={(row) => row.id}
              loading={isLoadingModal}
              checkboxSelection={true}
              autoHeight={false}
              onSelectionModelChange={(ids: GridSelectionModel) => {
                const visibleIds = new Set<string | number>(filteredTipologiasN2.map((r) => r.id));
                setSelecaoModalN2((prev) => {
                  const preservados = prev.filter((id) => !visibleIds.has(id));
                  return [...preservados, ...ids];
                });
              }}
              selectionModel={selecaoModalN2}
              pageSize={10}
              rowCount={filteredTipologiasN2.length}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setModalCriar({ isOpen: false }); setSelecaoModalN2([]); }}>CANCELAR</Button>
          <Button variant="contained" onClick={handleSalvarN2} disabled={isLoading || selecaoModalN2.length === 0}>
            {isLoading ? <CircularProgress size={20} /> : `SALVAR (${selecaoModalN2.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal CADASTROS N1 — tabela selecionável com 2 abas */}
      <Dialog
        open={modalCriar.isOpen && modalCriar.tipo === "referencia"}
        onClose={() => { setModalCriar({ isOpen: false }); setSelecaoModalN1([]); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxWidth: "900px" } }}
      >
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>
          Adicionar materiais para o CEM Desktop
        </DialogTitle>
        <DialogContent>
          <Tabs value={abaModalN1} onChange={(e, newValue) => setAbaModalN1(newValue)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2, ...tabStyle }}>
            <Tab label="HOMOLOGAÇÃO" value="HOMOLOGACAO" />
            <Tab label="MERCADO" value="MERCADO" />
          </Tabs>

          <Box sx={{ height: 400 }}>
            <StripedDataGrid
              columns={columnsModalN1}
              rows={filteredMateriaisN1}
              getRowId={(row) => row.id}
              loading={isLoadingModal}
              checkboxSelection={true}
              autoHeight={false}
              onSelectionModelChange={(ids: GridSelectionModel) => {
                const visibleIds = new Set<string | number>(filteredMateriaisN1.map((r) => r.id));
                setSelecaoModalN1((prev) => {
                  const preservados = prev.filter((id) => !visibleIds.has(id));
                  return [...preservados, ...ids];
                });
              }}
              selectionModel={selecaoModalN1}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setModalCriar({ isOpen: false }); setSelecaoModalN1([]); }}>CANCELAR</Button>
          <Button variant="contained" onClick={handleSalvarN1} disabled={isLoading || selecaoModalN1.length === 0}>
            {isLoading ? <CircularProgress size={20} /> : `SALVAR (${selecaoModalN1.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal APLICABILIDADES — tabela selecionável com 2 abas */}
      <Dialog
        open={modalCriar.isOpen && modalCriar.tipo === "aplicabilidade"}
        onClose={() => { setModalCriar({ isOpen: false }); setSelecaoModalAplic([]); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxWidth: "900px" } }}
      >
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>
          Adicionar materiais para o CEM Desktop - {empresaSelecionada?.nomeFantasia || empresaSelecionada?.razaoSocial || "EMPRESA"}
        </DialogTitle>
        <DialogContent>
          <Tabs value={abaModalAplic} onChange={(e, newValue) => setAbaModalAplic(newValue)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2, ...tabStyle }}>
            <Tab label="HOMOLOGAÇÃO" value="HOMOLOGACAO" />
            <Tab label="MERCADO" value="MERCADO" />
          </Tabs>

          <Box sx={{ height: 400 }}>
            <StripedDataGrid
              columns={columnsModalAplic}
              rows={filteredAplicDisp}
              getRowId={(row) => row.id}
              loading={isLoadingModal}
              checkboxSelection={true}
              autoHeight={false}
              onSelectionModelChange={(ids: GridSelectionModel) => {
                const visibleIds = new Set<string | number>(filteredAplicDisp.map((r) => r.id));
                setSelecaoModalAplic((prev) => {
                  const preservados = prev.filter((id) => !visibleIds.has(id));
                  return [...preservados, ...ids];
                });
              }}
              selectionModel={selecaoModalAplic}
              pageSize={10}
              rowCount={filteredAplicDisp.length}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setModalCriar({ isOpen: false }); setSelecaoModalAplic([]); }}>CANCELAR</Button>
          <Button variant="contained" onClick={handleSalvarAplic} disabled={isLoading || selecaoModalAplic.length === 0}>
            {isLoading ? <CircularProgress size={20} /> : `SALVAR (${selecaoModalAplic.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal TABELAS DE PREÇOS — HOMOLOGAÇÃO = Em Homologação + Homologada; MERCADO = Disponível Mercado (P3, 28/09/2026) */}
      <Dialog
        open={modalCriar.isOpen && modalCriar.tipo === "tabelaPreco"}
        onClose={() => { setModalCriar({ isOpen: false }); setSelecaoModalTabela([]); }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxWidth: "900px" } }}
      >
        <DialogTitle sx={{ color: "#3C90AD", fontWeight: 600 }}>
          Adicionar tabelas de preços para o CEM Desktop - {empresaSelecionada?.nomeFantasia || empresaSelecionada?.razaoSocial || "EMPRESA"}
        </DialogTitle>
        <DialogContent>
          <Tabs value={abaModalTabelaPreco} onChange={(e, newValue) => setAbaModalTabelaPreco(newValue)} sx={{ borderBottom: 1, borderColor: "divider", mb: 2, ...tabStyle }}>
            <Tab label="HOMOLOGAÇÃO" value="HOMOLOGACAO" />
            <Tab label="MERCADO" value="MERCADO" />
          </Tabs>

          <Box sx={{ height: 400 }}>
            <StripedDataGrid
              columns={columnsModalTabela}
              rows={filteredTabelaDisp}
              getRowId={(row) => row.id}
              loading={isLoadingModal}
              checkboxSelection={true}
              autoHeight={false}
              onSelectionModelChange={(ids: GridSelectionModel) => {
                const visibleIds = new Set<string | number>(filteredTabelaDisp.map((r) => r.id));
                setSelecaoModalTabela((prev) => {
                  const preservados = prev.filter((id) => !visibleIds.has(id));
                  return [...preservados, ...ids];
                });
              }}
              selectionModel={selecaoModalTabela}
              pageSize={10}
              rowCount={filteredTabelaDisp.length}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setModalCriar({ isOpen: false }); setSelecaoModalTabela([]); }}>CANCELAR</Button>
          <Button variant="contained" onClick={handleSalvarTabela} disabled={isLoading || selecaoModalTabela.length === 0}>
            {isLoading ? <CircularProgress size={20} /> : `SALVAR (${selecaoModalTabela.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Confirmar Remoção */}
      <ConfirmDeleteModal
        open={modalDelete.isOpen}
        onClose={() => setModalDelete({ isOpen: false })}
        onConfirm={handleRemoverLiberacao}
        title="Remover Liberação"
        description="Remover esta liberação impedirá o cliente de importar este conteúdo. Confirmar?"
        isLoadingDelete={isLoading}
      />

      <BackdropLoading isOpen={isLoading} />
    </DashboardStackCustom>
  );
};
